/**
 * The Pictures view's model (spec 026): the story's scenes in story order,
 * the By cast groups, the By scene frames and the words they show. Pure; the
 * slots come from the story's picture map (`storyStore.pictureMap`), the
 * scenes only give names, sprite order, default states and line text.
 */
import type { Dialog, PictureKind, PictureMap, PictureSlot, SlotUse, Sprite, StoryApiResponse, StorySceneWithScene } from '../../../../Shared/src/@types';
import {
  DEFAULT_BACKGROUND_NAME, isSlotFilled, isSlotNeeded, pictureSlotLabel, pictureSlug, pictureVariantNoun, slotsInUse, type PictureScene,
} from '../../../../Shared/src/pictures';
import { storySceneKey } from '../../../../Shared/src/storyOperations/document';
import { buildStoryRoute } from '../../utils/storyRoute';
import type { PicturesFilter } from './usePicturesNav';

/** A loaded scene of the story, as the map reads it, with its rows' ids. */
export interface PictureSceneRow extends PictureScene {
  storySceneId: number;
  sceneId: number;
}

/** The story's scene rows in story order: the route's stops, then the off-path scenes, then any not on the map. */
export function storySceneOrder(story: Pick<StoryApiResponse, 'graph' | 'storyScenes'>): StorySceneWithScene[] {
  const route = buildStoryRoute(story.graph, story.storyScenes);
  const ids = [...route.stops, ...route.offPath].filter((n) => n.kind === 'scene').map((n) => n.storySceneId);
  const byId = new Map(story.storyScenes.map((ss) => [ss.id, ss]));
  const out: StorySceneWithScene[] = [];
  const seen = new Set<number>();
  for (const id of ids) {
    const ss = byId.get(id);
    if (ss && !seen.has(id)) { out.push(ss); seen.add(id); }
  }
  for (const ss of story.storyScenes) if (!seen.has(ss.id)) { out.push(ss); seen.add(ss.id); }
  return out;
}

/** A scene row's display name. */
export const sceneRowName = (ss: Pick<StorySceneWithScene, 'sceneName' | 'sceneId'>): string =>
  ss.sceneName || `Scene #${ss.sceneId}`;

/** The scene key a story scene row has in every API response. */
export const sceneKeyOf = (storySceneId: number): string => storySceneKey(storySceneId);

const FAR = Number.MAX_SAFE_INTEGER;

/** Where a use first shows in the story: scene order, then sprite order (a background before every sprite). */
function usePosition(use: SlotUse, scenes: readonly PictureScene[]): [number, number] {
  const at = scenes.findIndex((s) => s.key === use.scene);
  if (at < 0) return [FAR, FAR];
  if (use.kind === 'background') return [at, -1];
  const sprite = (scenes[at].doc.sprites ?? []).findIndex((sp) => sp.id === use.spriteId);
  return [at, sprite < 0 ? FAR : sprite];
}

const comparePos = (a: [number, number], b: [number, number]) => a[0] - b[0] || a[1] - b[1];

function firstPosition(slot: PictureSlot, scenes: readonly PictureScene[]): [number, number] {
  let best: [number, number] = [FAR, FAR];
  for (const u of slot.uses) {
    const p = usePosition(u, scenes);
    if (comparePos(p, best) < 0) best = p;
  }
  return best;
}

export interface CastGroup {
  /** `<kind>|<slug(subject)>` (`background|<slug>` for a background with states), or `backgrounds`. */
  id: string;
  kind: PictureKind;
  /** The subject, or "Backgrounds". */
  title: string;
  /** "<k> expressions" (character), "<k> states" (prop, or a background with states, spec 040 D-5), "<k> pictures" (full picture) or "Scene backdrops" (D-29). */
  subtitle: string;
  slots: PictureSlot[];
}

/**
 * The By cast groups: one per character, prop or full picture subject in the order the
 * story first shows it (scenes in story order, then sprite order), `neutral`
 * first and then by first use; then "Backgrounds". Only slots in use.
 */
export function castGroups(map: PictureMap | null | undefined, scenes: readonly PictureScene[]): CastGroup[] {
  const inUse = slotsInUse(map);
  const order = new Map(inUse.map((s, i) => [s.id, i]));
  const pos = new Map(inUse.map((s) => [s.id, firstPosition(s, scenes)]));
  const bySlot = (a: PictureSlot, b: PictureSlot) =>
    comparePos(pos.get(a.id)!, pos.get(b.id)!) || order.get(a.id)! - order.get(b.id)!;

  const groups = new Map<string, CastGroup>();
  for (const slot of inUse) {
    if (slot.kind === 'background') continue;
    const id = `${slot.kind}|${pictureSlug(slot.subject)}`;
    let g = groups.get(id);
    if (!g) {
      g = { id, kind: slot.kind, title: slot.subject, subtitle: '', slots: [] };
      groups.set(id, g);
    }
    g.slots.push(slot);
  }
  const isNeutral = (s: PictureSlot) => pictureSlug(s.variant) === 'neutral';
  const people = [...groups.values()];
  for (const g of people) {
    g.slots.sort((a, b) => (isNeutral(a) === isNeutral(b) ? bySlot(a, b) : isNeutral(a) ? -1 : 1));
    g.subtitle = `${g.slots.length} ${pictureVariantNoun(g.kind, g.slots.length)}`;
  }
  const groupPos = (g: CastGroup) => g.slots.map((s) => pos.get(s.id)!).sort(comparePos)[0];
  const groupOrder = (g: CastGroup) => Math.min(...g.slots.map((s) => order.get(s.id)!));
  people.sort((a, b) => comparePos(groupPos(a), groupPos(b)) || groupOrder(a) - groupOrder(b));

  // The backdrops: backgrounds with no states share "Backgrounds"; a
  // background with states is its own group, one card per state (spec 040 D-5).
  const backgrounds = inUse.filter((s) => s.kind === 'background').sort(bySlot);
  const plain = backgrounds.filter((s) => !s.variant.trim());
  if (plain.length) {
    people.push({ id: 'backgrounds', kind: 'background', title: 'Backgrounds', subtitle: 'Scene backdrops', slots: plain });
  }
  const withStates = new Map<string, CastGroup>();
  for (const slot of backgrounds) {
    if (!slot.variant.trim()) continue;
    const id = `background|${pictureSlug(slot.subject)}`;
    let g = withStates.get(id);
    if (!g) {
      g = { id, kind: 'background', title: slot.subject, subtitle: '', slots: [] };
      withStates.set(id, g);
    }
    g.slots.push(slot);
  }
  // A state group's cards follow the background's own order of states.
  const stateIndex = (slot: PictureSlot): number => {
    for (const u of slot.uses) {
      const scene = scenes.find((s) => s.key === u.scene);
      const at = (scene?.doc.background?.states ?? []).findIndex((st) => st.name === u.state);
      if (at >= 0) return at;
    }
    return FAR;
  };
  for (const g of withStates.values()) {
    g.slots.sort((a, b) => stateIndex(a) - stateIndex(b) || bySlot(a, b));
    g.subtitle = `${g.slots.length} ${g.slots.length === 1 ? 'state' : 'states'}`;
    people.push(g);
  }
  return people;
}

/** The slots a filter keeps. */
export function filterSlots(slots: readonly PictureSlot[], filter: PicturesFilter): PictureSlot[] {
  if (filter === 'needed') return slots.filter(isSlotNeeded);
  if (filter === 'filled') return slots.filter(isSlotFilled);
  return [...slots];
}

/** The By cast groups after a filter, empty groups hidden. */
export function filteredGroups(groups: readonly CastGroup[], filter: PicturesFilter): CastGroup[] {
  return groups
    .map((g) => ({ ...g, slots: filterSlots(g.slots, filter) }))
    .filter((g) => g.slots.length > 0);
}

/** A line's first four words, as a By scene caption quotes them. */
export function firstWords(text: string, n = 4): string {
  return text.trim().split(/\s+/).filter(Boolean).slice(0, n).join(' ');
}

function lineText(dialogs: readonly Dialog[] | null | undefined, use: SlotUse): string {
  const d = (dialogs ?? []).find((x) => x.id === use.dialogId);
  return d?.lines.find((l) => l.id === use.lineId)?.text ?? '';
}

export interface SceneFrame {
  slot: PictureSlot;
  /** "backdrop", `line <n>, “<four words>…”`, "default" or "not on a line yet". */
  caption: string;
}

/**
 * A scene's frames in beat order (the trailer view): the background, then
 * each slot at its first line use in line order, then the default uses of
 * characters not yet shown, then the rest ("not on a line yet"). A slot
 * appears once per scene.
 */
export function sceneFrames(scene: PictureScene, map: PictureMap | null | undefined): SceneFrame[] {
  const inUse = slotsInUse(map);
  const frames: SceneFrame[] = [];
  const shown = new Set<string>();
  const add = (slot: PictureSlot, caption: string) => {
    if (shown.has(slot.id)) return;
    shown.add(slot.id);
    frames.push({ slot, caption });
  };
  const here = (u: SlotUse) => u.scene === scene.key;

  for (const slot of inUse) {
    if (slot.uses.some((u) => here(u) && u.kind === 'background')) add(slot, 'backdrop');
  }

  const lineUses = inUse
    .flatMap((slot) => slot.uses.filter((u) => here(u) && u.kind === 'line').map((u) => ({ slot, use: u })))
    .sort((a, b) => (a.use.line ?? 0) - (b.use.line ?? 0));
  for (const { slot, use } of lineUses) {
    add(slot, `line ${use.line}, “${firstWords(lineText(scene.doc.dialogs, use))}…”`);
  }

  const sprites: readonly Sprite[] = scene.doc.sprites ?? [];
  const spriteUses = inUse
    .flatMap((slot) => slot.uses.filter((u) => here(u) && u.kind === 'sprite').map((u) => ({ slot, use: u })))
    .map((x) => ({ ...x, at: sprites.findIndex((sp) => sp.id === x.use.spriteId) }))
    .sort((a, b) => a.at - b.at);
  const isDefault = (use: SlotUse) => sprites.find((sp) => sp.id === use.spriteId)?.defaultState === use.state;
  for (const { slot, use } of spriteUses) if (isDefault(use)) add(slot, 'default');
  for (const { slot } of spriteUses) add(slot, 'not on a line yet');
  return frames;
}

/** "A", "A and B", "A, B and C". */
export function joinNames(names: readonly string[]): string {
  if (names.length <= 1) return names[0] ?? '';
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

/** The By scene note: the first needed slot used in two or more scenes, with those scenes' names. */
export function sharedSlotNote(
  map: PictureMap | null | undefined,
  scenes: readonly PictureScene[],
): { slot: PictureSlot; text: string } | null {
  for (const slot of slotsInUse(map)) {
    if (!isSlotNeeded(slot)) continue;
    const keys = [...new Set(slot.uses.map((u) => u.scene))];
    if (keys.length < 2) continue;
    const names = keys.map((k) => {
      const s = scenes.find((x) => x.key === k);
      return s ? s.doc.name || s.name : k;
    });
    const label = pictureSlotLabel(slot);
    return { slot, text: `A slot used in several scenes is filled once. Filling ${label} fills it in ${joinNames(names)}.` };
  }
  return null;
}

/** The first needed slot in By cast order. */
export function firstNeededSlot(groups: readonly CastGroup[]): PictureSlot | undefined {
  for (const g of groups) {
    const s = g.slots.find(isSlotNeeded);
    if (s) return s;
  }
  return undefined;
}

/** "Placed in 1 spot." / "Placed in 3 spots." (upload: "Added to your library and placed in …"). */
export function placedText(places: number, uploaded: boolean): string {
  const spots = `${places} ${places === 1 ? 'spot' : 'spots'}`;
  return uploaded ? `Added to your library and placed in ${spots}.` : `Placed in ${spots}.`;
}

/**
 * D-23: the scenes whose background has no name (absent, blank or the
 * default) and no picture (no base image, no layers), and that no slot of
 * the map has a `background` use in, in the order given (story order).
 * Naming such a background is exactly what makes it a slot (D-21). A scene
 * whose content has not loaded (no `background`) gives no row.
 */
export function unnamedBackgroundScenes<S extends PictureScene>(map: PictureMap | null | undefined, scenes: readonly S[]): S[] {
  const used = new Set<string>();
  for (const slot of map?.slots ?? []) for (const u of slot.uses) if (u.kind === 'background') used.add(u.scene);
  return scenes.filter((scene) => {
    const bg = scene.doc.background;
    if (!bg) return false;
    const name = (bg.name ?? '').trim();
    if (name && name !== DEFAULT_BACKGROUND_NAME) return false;
    if (typeof bg.baseMediaId === 'number' && bg.baseMediaId > 0) return false;
    if ((bg.layers ?? []).length > 0) return false;
    return !used.has(scene.key);
  });
}
